import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Typography,
  Box,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { PlaceWithGTTParams } from "../commons/interface";
import { useLivePrices } from "../hooks/useLivePrices";
import { roundToTick, isValidStopLoss, MAX_SL_BUFFER, formatPrice } from "../utils/utils";
import { useMarketStatus } from "../hooks/useMarketStatus";
import StaleDataAlert from "./StaleDataAlert";

export default function OrderModal({
  open,
  onClose,
  onSubmit,
  stock,
  currentPrice,
  instrumentToken,
  atr,
  volume,
  tickSize,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PlaceWithGTTParams) => void;
  stock: string;
  currentPrice: number;
  instrumentToken: number;
  atr?: number;
  volume?: number;
  tickSize: number;
}) {
  const [quantity, setQuantity] = useState(1);
  const [amount, setAmount] = useState<number>();
  const [price, setPrice] = useState(0);
  const [orderType, setOrderType] =
    useState<PlaceWithGTTParams["orderType"]>("MARKET"); // MARKET or LIMIT
  const [variety, setVariety] =
    useState<PlaceWithGTTParams["variety"]>("regular"); // regular or amo
  const [enableGTT, setEnableGTT] = useState(true);
  const [targetPercent, setTargetPercent] = useState(6);
  const [stoplossPercent, setStoplossPercent] = useState(3);
  const [snackbar, setSnackbar] = useState<any>();
  const [slError, setSlError] = useState<any>();
  const { prices, getLivePrice } = useLivePrices();
  const { marketStatus } = useMarketStatus();
  const isInitialMount = useRef(true);

  // Set ATR-based defaults and calculate quantity
  useEffect(() => {
    if (isInitialMount.current) {
      if (atr) {
        // We keep the quantity calculation but don't override SL/Target with ATR anymore
        // as the user wants fixed defaults of 3% SL and 6% Target.
      }
      const ltp = prices[instrumentToken]?.price || currentPrice;
      console.log("ltp", ltp, stoplossPercent);

      if (ltp && stoplossPercent) {
        const riskQty = Math.floor(10000 / (ltp * (stoplossPercent / 100)));
        const valueQty = Math.floor(200000 / ltp);
        const suggestedQty = Math.min(riskQty, valueQty);
        console.log("suggestedQty", suggestedQty);

        setQuantity(suggestedQty > 0 ? suggestedQty : 1);
      }
      isInitialMount.current = false; // Set flag to false after initial setup
    }
  }, [atr, prices[instrumentToken]?.price, currentPrice, quantity]); // Removed stoplossPercent from dependencies

  useEffect(() => {
    if (instrumentToken) getLivePrice([instrumentToken]);
  }, [instrumentToken]);

  const handlePlaceOrder = async () => {
    const orderData: PlaceWithGTTParams = {
      symbol: stock,
      quantity: quantity,
      buyPrice: orderType === "LIMIT" ? price : 0,
      orderType: orderType,
      gttTargetPercent: targetPercent,
      gttStoplossPercent: stoplossPercent,
      exchange: "NSE",
      product: "CNC",
      tickSize: tickSize,
      variety: variety,
    };

    try {
      const ltp = prices[instrumentToken]?.price || currentPrice;
      const risk = (ltp || 0) * quantity * (stoplossPercent / 100);
      const totalAmount = quantity * (ltp || 0);
      
      if (risk > 10000) {
        setSnackbar({
          open: true,
          message: `Risk ₹${formatPrice(risk)} exceeds ₹10,000. Adjust quantity or stoploss %.`,
        });
        return;
      }
      if (totalAmount > 200000) {
        setSnackbar({
          open: true,
          message: `Total amount ₹${formatPrice(totalAmount)} exceeds ₹2,00,000. Adjust quantity.`,
        });
        return;
      }

      const tick_Size = tickSize || 0.05;
      const stopLossTrigger = roundToTick(
        (ltp || 0) * (1 - stoplossPercent / 100),
        tick_Size
      );
      
      const buffer = 1.0;
      const stopLossLimit = roundToTick(
        stopLossTrigger - buffer,
        tick_Size
      );

      if (!isValidStopLoss(stopLossTrigger, stopLossLimit)) {
        setSnackbar({
          open: true,
          message: `Stoploss limit price exceeds ₹${MAX_SL_BUFFER} from trigger. Please adjust stoploss % or price.`,
        });
        return;
      }

      await onSubmit(orderData);
      onClose();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "❌ Failed to place order",
      });
    }
  };

  useEffect(() => {
    const ltp = prices[instrumentToken]?.price || currentPrice;
    setAmount(quantity * (ltp || 0));
  }, [quantity, prices[instrumentToken]?.price, currentPrice]);

  useEffect(() => {
    const ltp = currentPrice || prices[instrumentToken]?.price;
    const error = validateSLRange(ltp || 0, stoplossPercent);
    setSlError(error);
  }, [prices[instrumentToken]?.price, stoplossPercent, currentPrice]);

  const validateSLRange = (
    lastPrice: number,
    stopLossPercent: number,
    slBuffer: number = 0.95
  ): string | null => {
    const stopLossTrigger = roundToTick(
      lastPrice * (1 - stopLossPercent / 100),
      tickSize || 0.5
    );
    const stopLossLimit = roundToTick(
      stopLossTrigger - slBuffer,
      tickSize || 0.05
    );
    const diff = Math.abs(stopLossTrigger - stopLossLimit);
    return diff > 1.5
      ? `Stop Loss difference ₹${formatPrice(diff)} exceeds allowed ₹1.5. Adjust stoploss %.`
      : null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Place Buy Order - {stock}</DialogTitle>
      {slError && (
        <Box sx={{ px: 3, pt: 1 }}>
          <Typography color="error" variant="body2">{slError}</Typography>
        </Box>
      )}
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">Current Price : <strong>{currentPrice || prices[instrumentToken]?.price || "-"}</strong></Typography>
          <Typography variant="body2">ATR: {formatPrice(atr)}%</Typography>
          <Typography variant="body2">Volume: ₹{formatPrice(volume)} Cr</Typography>
          <Typography variant="body2">Suggested Quantity: {quantity}</Typography>
          <Typography variant="body2">Total Amount: ₹{amount?.toLocaleString()} Rupees</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
            /> 
          </Grid>

          <Grid size={6}>
            <TextField
              select
              fullWidth
              label="Order Type"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value as any)}
            >
              <MenuItem value="MARKET">Market</MenuItem>
              <MenuItem value="LIMIT">Limit</MenuItem>
            </TextField>
          </Grid>

          <Grid size={6}>
            <TextField
              select
              fullWidth
              label="Variety"
              value={variety}
              onChange={(e) => setVariety(e.target.value as any)}
            >
              <MenuItem value="regular">Regular</MenuItem>
              <MenuItem value="amo">AMO</MenuItem>
            </TextField>
          </Grid>

          {orderType === "LIMIT" && (
            <Grid size={12}>
              <TextField
                fullWidth
                label="Limit Price"
                type="number"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
              />
            </Grid>
          )}

          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableGTT}
                  onChange={(e) => setEnableGTT(e.target.checked)}
                />
              }
              label="Auto-sell with GTT (Target + Stoploss)"
            />
          </Grid>

          <Grid size={6}>
            <TextField
              fullWidth
              label="Target (%)"
              type="number"
              value={targetPercent}
              onChange={(e) => setTargetPercent(Number(e.target.value))}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Stoploss (%)"
              type="number"
              value={stoplossPercent}
              onChange={(e) => setStoplossPercent(Number(e.target.value))}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <StaleDataAlert
            isStale={marketStatus?.dataHealth?.isStale}
            lastDataDate={marketStatus?.dataHealth?.lastDataDate}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!!slError}
          onClick={handlePlaceOrder}
        >
          Buy
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar?.message}
      />
    </Dialog>
  );
}
